import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Download } from "lucide-react";
import CTABanner from "../components/CTABanner";
import StarMotif from "../components/StarMotif";
import CaseStudyDossierModal from "../components/CaseStudyDossierModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CaseStudies = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dossierOpen, setDossierOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/content/case-studies`)
      .then((res) => { if (alive) setItems(res.data?.case_studies || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  return (
    <>
      <section className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA]">Case studies</div>
          <h1 className="ek-heading text-4xl md:text-5xl lg:text-6xl mt-4 text-white max-w-4xl leading-tight" data-testid="case-studies-headline">
            Outcome-led transformation, proven in complex environments.
          </h1>
          <p className="text-zinc-200 font-light text-lg md:text-xl leading-relaxed mt-8 max-w-3xl">
            Senior-level delivery where technology, operations, suppliers and risk intersect — stabilising the core,
            simplifying delivery, strengthening governance and making benefits visible.
          </p>
          <button
            onClick={() => setDossierOpen(true)}
            className="inline-flex items-center gap-2 mt-9 bg-white text-[#0B0B0C] font-medium text-sm px-5 py-2.5 rounded-full hover:bg-zinc-100 transition-colors"
            data-testid="download-dossier-btn"
          >
            <Download size={16} /> Download all case studies (PDF)
          </button>
        </div>
      </section>

      <section className="bg-[#F2F2F3] ek-section">
        <div className="ek-container">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="ek-card animate-pulse h-72 bg-white" />)}
            </div>
          ) : items.length === 0 ? (
            <p className="ek-body text-lg max-w-3xl" data-testid="case-studies-empty">Case studies are being prepared — check back shortly.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ek-stagger">
              {items.map((cs) => (
                <Link
                  key={cs.slug}
                  to={`/case-studies/${cs.slug}`}
                  className="ek-card block group flex flex-col"
                  data-testid={`case-study-card-${cs.slug}`}
                >
                  <div className="flex items-start justify-between">
                    <StarMotif size={18} color="#7737F6" />
                    <div className="text-right">
                      <div className="ek-heading text-2xl text-black leading-none">{cs.metric_value}</div>
                    </div>
                  </div>
                  <div className="mt-1 text-[11px] font-semibold tracking-[0.14em] text-zinc-500 uppercase text-right leading-tight">{cs.metric_label}</div>
                  <div className="mt-5 text-[11px] font-semibold tracking-[0.16em] text-[#7737F6] uppercase">{cs.sector}</div>
                  <h3 className="ek-heading text-xl md:text-[22px] text-black mt-2 leading-snug">{cs.title}</h3>
                  <p className="ek-body text-[14.5px] mt-3 line-clamp-3">{cs.business_situation}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {(cs.services_tags || []).slice(0, 3).map((t) => (
                      <span key={t} className="text-[10.5px] font-medium text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-full">{t}</span>
                    ))}
                  </div>
                  <div className="mt-auto pt-5 inline-flex items-center gap-2 text-[#7737F6] text-sm font-medium">
                    Read case study <ArrowRight size={14} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <CTABanner />
      <CaseStudyDossierModal open={dossierOpen} onClose={() => setDossierOpen(false)} />
    </>
  );
};

export default CaseStudies;
